import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import useColors from "../../../../../../../api/hooks/useColors";
import CustomTooltip from "../../../../../../../components/CustomTooltip";
import {
  getDefaultVersionSettings,
  getWindowsOSNameFromVersion,
} from "./osVersionHelpers";

const OSVersionSettingsDialog = ({ open, onClose, settings, onSave }) => {
  const colors = useColors();
  const [localSettings, setLocalSettings] = useState(settings);

  // Initialize the Windows version states if not present
  const [windowsVersions, setWindowsVersions] = useState(() => {
    // Group Windows versions by OS category (Windows 10, Windows 11)
    const groupedVersions = Object.keys(localSettings.windows || {}).reduce(
      (acc, version) => {
        const osName = getWindowsOSNameFromVersion(version);
        if (!acc[osName]) {
          acc[osName] = {
            enabled: true,
            minBuildNumber: getMinBuildNumber(osName, localSettings.windows),
            versions: {},
          };
        }
        acc[osName].versions[version] = localSettings.windows[version];
        return acc;
      },
      {}
    );
    return groupedVersions;
  });

  // Get minimum build number for a Windows OS version
  function getMinBuildNumber(osName, windowsSettings) {
    const buildNumbers = Object.keys(windowsSettings || {})
      .filter((version) => getWindowsOSNameFromVersion(version) === osName)
      .map((version) => parseInt(version.split(".")[2], 10));

    // Return the minimum currently enabled build for this OS
    if (buildNumbers.length) {
      // Find the minimum enabled build
      const enabledBuilds = buildNumbers.filter(
        (_, i) => windowsSettings[`10.0.${buildNumbers[i]}`]
      );
      return enabledBuilds.length
        ? Math.min(...enabledBuilds)
        : Math.min(...buildNumbers);
    }
    return osName === "Windows 11" ? 22000 : 19044;
  }

  const handleSave = () => {
    // Convert our grouped structure back to the flat structure expected by the parent
    const updatedWindowsSettings = {};

    Object.entries(windowsVersions).forEach(([osName, osSettings]) => {
      Object.keys(osSettings.versions).forEach((version) => {
        // A version is "latest" if it's enabled and its build number >= the min build number
        const versionBuildNumber = parseInt(version.split(".")[2], 10);
        updatedWindowsSettings[version] =
          osSettings.enabled && versionBuildNumber >= osSettings.minBuildNumber;
      });
    });

    onSave({
      ...localSettings,
      windows: updatedWindowsSettings,
    });

    onClose();
  };

  const handleReset = () => {
    const defaultSettings = getDefaultVersionSettings();
    setLocalSettings(defaultSettings);

    // Reset the Windows version groupings based on default settings
    const groupedVersions = Object.keys(defaultSettings.windows || {}).reduce(
      (acc, version) => {
        const osName = getWindowsOSNameFromVersion(version);
        if (!acc[osName]) {
          acc[osName] = {
            enabled: true,
            minBuildNumber: getMinBuildNumber(osName, defaultSettings.windows),
            versions: {},
          };
        }
        acc[osName].versions[version] = defaultSettings.windows[version];
        return acc;
      },
      {}
    );
    setWindowsVersions(groupedVersions);
  };

  const handleWindowsOSToggle = (osName, enabled) => {
    setWindowsVersions((prev) => ({
      ...prev,
      [osName]: {
        ...prev[osName],
        enabled,
      },
    }));
  };

  const handleMinBuildNumberChange = (osName, value) => {
    const buildNumber = value !== "" ? parseInt(value, 10) : 0;
    setWindowsVersions((prev) => ({
      ...prev,
      [osName]: {
        ...prev[osName],
        minBuildNumber: buildNumber,
      },
    }));
  };

  const handleMacVersionChange = (majorVersion, field, value) => {
    setLocalSettings({
      ...localSettings,
      mac: {
        ...localSettings.mac,
        [majorVersion]: {
          ...localSettings.mac[majorVersion],
          [field]: value !== "" ? Number(value) : 0,
        },
      },
    });
  };

  const handleMacVersionToggle = (majorVersion, enabled) => {
    setLocalSettings({
      ...localSettings,
      mac: {
        ...localSettings.mac,
        [majorVersion]: {
          ...localSettings.mac[majorVersion],
          enabled,
        },
      },
    });
  };

  // Get sample build from each OS version category to display
  const getSampleBuild = (osName) => {
    const versions = Object.keys(windowsVersions[osName]?.versions || {});
    if (versions.length) {
      const buildNumbers = versions.map((v) => v.split(".")[2]);
      return buildNumbers.sort().reverse()[0] || ""; // Return newest build
    }
    return "";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.common_white,
          boxShadow: `0px 0px 10px ${colors.grey_24percent}`,
          borderRadius: "10px",
        },
      }}
    >
      <DialogTitle sx={{ paddingBottom: 0, pt: 3 }}>
        <Typography variant="subtitle1" color={colors.text_primary}>
          OS Version Settings
        </Typography>
        <Typography variant="caption" color={colors.text_secondary}>
          Configure which versions are considered "latest" when aggregation is
          enabled
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          "&:first-of-type": {
            paddingTop: "24px",
          },
          px: 3,
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography
              variant="subtitle2"
              color={colors.text_primary}
              sx={{ mr: 1 }}
            >
              Windows
            </Typography>
            <CustomTooltip
              title="Configure minimum Windows build numbers considered 'latest'"
              placement="top"
            >
              <InfoIcon
                fontSize="small"
                sx={{ color: colors.text_secondary, fontSize: 16 }}
              />
            </CustomTooltip>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {/* Windows version settings */}
          {Object.entries(windowsVersions)
            .sort((a, b) => b[0].localeCompare(a[0]))
            .map(([osName, osSettings]) => (
              <Box
                key={osName}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  mb: 3,
                  gap: 2,
                }}
              >
                <FormControl fullWidth>
                  <InputLabel
                    id={`windows-${osName}-label`}
                    sx={{
                      color: colors.text_secondary,
                      "&.Mui-focused": {
                        color: colors.primary,
                      },
                    }}
                  >
                    {`${osName} (e.g. Build ${getSampleBuild(osName)})`}
                  </InputLabel>
                  <Select
                    labelId={`windows-${osName}-label`}
                    value={osSettings.enabled.toString()}
                    label={`${osName} (e.g. Build ${getSampleBuild(osName)})`}
                    onChange={(e) =>
                      handleWindowsOSToggle(osName, e.target.value === "true")
                    }
                    sx={{
                      ".MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.grey_24percent,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.primary,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.primary,
                      },
                      color: colors.text_primary,
                      borderRadius: "6px",
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: colors.common_white,
                          boxShadow: `0px 2px 6px ${colors.divider2}`,
                          borderRadius: "6px",
                        },
                      },
                    }}
                  >
                    <MenuItem value="true">Include as latest</MenuItem>
                    <MenuItem value="false">Exclude</MenuItem>
                  </Select>
                </FormControl>

                {osSettings.enabled && (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      label="OS Version"
                      value={osName}
                      disabled
                      size="small"
                      sx={{
                        width: "50%",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: colors.grey_24percent,
                          },
                          "&:hover fieldset": {
                            borderColor: colors.primary,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: colors.primary,
                          },
                          borderRadius: "6px",
                        },
                        "& .MuiFormLabel-root": {
                          color: colors.text_secondary,
                          "&.Mui-focused": {
                            color: colors.primary,
                          },
                        },
                        "& input": {
                          color: colors.text_primary,
                        },
                      }}
                    />
                    <TextField
                      label="Min build number"
                      type="number"
                      size="small"
                      value={osSettings.minBuildNumber}
                      onChange={(e) =>
                        handleMinBuildNumberChange(osName, e.target.value)
                      }
                      InputProps={{ inputProps: { min: 0 } }}
                      sx={{
                        width: "50%",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: colors.grey_24percent,
                          },
                          "&:hover fieldset": {
                            borderColor: colors.primary,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: colors.primary,
                          },
                          borderRadius: "6px",
                        },
                        "& .MuiFormLabel-root": {
                          color: colors.text_secondary,
                          "&.Mui-focused": {
                            color: colors.primary,
                          },
                        },
                        "& input": {
                          color: colors.text_primary,
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            ))}
        </Box>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography
              variant="subtitle2"
              color={colors.text_primary}
              sx={{ mr: 1 }}
            >
              macOS
            </Typography>
            <CustomTooltip
              title="Configure minimum versions for macOS. Devices with lower versions will be marked as outdated."
              placement="top"
            >
              <InfoIcon
                fontSize="small"
                sx={{ color: colors.text_secondary, fontSize: 16 }}
              />
            </CustomTooltip>
          </Box>
          <Divider sx={{ mb: 3 }} />
          {Object.keys(localSettings.mac).map((majorVersion) => (
            <Box
              key={majorVersion}
              sx={{
                display: "flex",
                flexDirection: "column",
                mb: 3,
                gap: 2,
              }}
            >
              <FormControl fullWidth>
                <InputLabel
                  id={`mac-${majorVersion}-label`}
                  sx={{
                    color: colors.text_secondary,
                    "&.Mui-focused": {
                      color: colors.primary,
                    },
                  }}
                >
                  {`macOS ${majorVersion} (${localSettings.mac[majorVersion].name})`}
                </InputLabel>
                <Select
                  labelId={`mac-${majorVersion}-label`}
                  value={localSettings.mac[majorVersion].enabled.toString()}
                  label={`macOS ${majorVersion} (${localSettings.mac[majorVersion].name})`}
                  onChange={(e) =>
                    handleMacVersionToggle(
                      majorVersion,
                      e.target.value === "true"
                    )
                  }
                  sx={{
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.grey_24percent,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.primary,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.primary,
                    },
                    color: colors.text_primary,
                    borderRadius: "6px",
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: colors.common_white,
                        boxShadow: `0px 2px 6px ${colors.divider2}`,
                        borderRadius: "6px",
                      },
                    },
                  }}
                >
                  <MenuItem value="true">Include as latest</MenuItem>
                  <MenuItem value="false">Exclude</MenuItem>
                </Select>
              </FormControl>

              {localSettings.mac[majorVersion].enabled && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="Min major version"
                    type="number"
                    size="small"
                    value={majorVersion}
                    disabled
                    sx={{
                      width: "33%",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: colors.grey_24percent,
                        },
                        "&:hover fieldset": {
                          borderColor: colors.primary,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: colors.primary,
                        },
                        borderRadius: "6px",
                      },
                      "& .MuiFormLabel-root": {
                        color: colors.text_secondary,
                        "&.Mui-focused": {
                          color: colors.primary,
                        },
                      },
                      "& input": {
                        color: colors.text_primary,
                      },
                    }}
                  />
                  <TextField
                    label="Min minor version"
                    type="number"
                    size="small"
                    value={localSettings.mac[majorVersion].minorVersion}
                    onChange={(e) =>
                      handleMacVersionChange(
                        majorVersion,
                        "minorVersion",
                        e.target.value
                      )
                    }
                    InputProps={{ inputProps: { min: 0 } }}
                    sx={{
                      width: "33%",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: colors.grey_24percent,
                        },
                        "&:hover fieldset": {
                          borderColor: colors.primary,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: colors.primary,
                        },
                        borderRadius: "6px",
                      },
                      "& .MuiFormLabel-root": {
                        color: colors.text_secondary,
                        "&.Mui-focused": {
                          color: colors.primary,
                        },
                      },
                      "& input": {
                        color: colors.text_primary,
                      },
                    }}
                  />
                  <TextField
                    label="Min patch version"
                    type="number"
                    size="small"
                    value={localSettings.mac[majorVersion].patchVersion}
                    onChange={(e) =>
                      handleMacVersionChange(
                        majorVersion,
                        "patchVersion",
                        e.target.value
                      )
                    }
                    InputProps={{ inputProps: { min: 0 } }}
                    sx={{
                      width: "33%",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: colors.grey_24percent,
                        },
                        "&:hover fieldset": {
                          borderColor: colors.primary,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: colors.primary,
                        },
                        borderRadius: "6px",
                      },
                      "& .MuiFormLabel-root": {
                        color: colors.text_secondary,
                        "&.Mui-focused": {
                          color: colors.primary,
                        },
                      },
                      "& input": {
                        color: colors.text_primary,
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>

      <Box
        sx={{
          justifyContent: "space-between",
          display: "flex",
          margin: "0 24px 20px",
          mt: 2,
        }}
      >
        <Button
          variant="text"
          onClick={handleReset}
          sx={{
            backgroundColor: colors.button_text_inherit_enabled_bg,
            color: colors.button_text_inherit_enabled_text,
            borderRadius: "10px",
            minWidth: "64px",
            padding: "7px 14.5px",
            "&:hover": {
              backgroundColor: colors.button_text_inherit_hovered_bg,
            },
          }}
        >
          <Typography variant="overline">Reset to Default</Typography>
        </Button>
        <DialogActions>
          <Button
            variant="text"
            onClick={onClose}
            sx={{
              backgroundColor: colors.button_text_inherit_enabled_bg,
              color: colors.button_text_inherit_enabled_text,
              borderRadius: "10px",
              minWidth: "64px",
              padding: "7px 14.5px",
              marginRight: 1,
              "&:hover": {
                backgroundColor: colors.button_text_inherit_hovered_bg,
              },
            }}
          >
            <Typography variant="overline">Cancel</Typography>
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              backgroundColor: colors.button_contained_primary_enabled_bg,
              color: colors.button_contained_primary_enabled_text,
              borderRadius: "10px",
              minWidth: "64px",
              padding: "7px 14.5px",
              "&:hover": {
                backgroundColor: colors.button_contained_primary_hovered_bg,
              },
            }}
          >
            <Typography variant="overline">Save</Typography>
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default OSVersionSettingsDialog;
